import { useTheme } from '@mui/material';
import type { IAccordionData } from '@rumblefish/ui/src/components/Rumblefish23Theme/Accordion/CustomizedAccordion.types';

export const faqData = {
  mainText: `FAQ`,
  description: `Stellar Product Development Services with Rumble Fish`,
};

export const AccordionData = (): IAccordionData[] => {
  const theme = useTheme();
  return [
    {
      title: 'Q: What programming languages are used to build on Stellar?',
      description: `A: Soroban smart contracts are written in Rust. For applications and integrations, Stellar offers official SDKs in JavaScript/TypeScript, Python, Go, Java, and more, so most teams can build with the stack they already know.`,
      gradientOnFocus: theme.mixins.cyanGradient?.background as string,
    },
    {
      title: 'Q: How does Stellar compare to Ethereum for payments and DeFi?',
      description: `A: Stellar is built specifically for payments and financial transactions, with fast finality, very low fees, native asset issuance, and a built-in DEX. Ethereum prioritizes general-purpose flexibility. For high-volume, low-cost financial applications, Stellar is often the better fit.`,
      gradientOnFocus: theme.mixins.yellowGradient?.background as string,
    },
    {
      title: 'Q: Can I build smart contracts on Stellar?',
      description: `A: Yes, through Soroban, Stellar's Rust-based smart contract platform. It uses resource metering for predictable fees, includes built-in authorization primitives, and is designed for financial use cases.`,
      gradientOnFocus: theme.mixins.violetGradient?.background as string,
    },
    {
      title: 'Q: How much does it cost to transact on Stellar?',
      description: `A: Classic Stellar transactions cost fractions of a cent. Soroban uses a resource-metered fee model that charges based on the compute and storage a transaction uses, keeping costs low and predictable for high-volume applications.`,
      gradientOnFocus: theme.mixins.grapeGradient?.background as string,
    },
    {
      title: 'Q: How long does it take to develop a Stellar application?',
      description: `A: It depends on complexity. A focused payment or anchor integration might take four to six weeks, while a full DeFi platform with custom Soroban contracts can run three to nine months. We usually start with an MVP to reach the market sooner, then iterate based on real user feedback rather than assumptions.`,
      gradientOnFocus: theme.mixins.purpleGradient?.background as string,
    },
    {
      title: 'Q: Is Stellar suitable for enterprise and institutional use?',
      description: `A: Yes. Stellar has been live since 2015, with an established global anchor network, native compliance tooling, and partners such as MoneyGram using it for real money movement. Its fast settlement, low costs, and built-in financial primitives suit it well to institutional use.`,
      gradientOnFocus: theme.mixins.magentaGradient?.background as string,
    },
    {
      title: "Q: What's the difference between Soroban and EVM smart contracts?",
      description: `A: Soroban is built in Rust, uses fine-grained resource metering rather than a single gas figure, and addresses state bloat directly through a state archival model. The result is lower fees and more predictable performance for financial workloads, compared with the higher and more variable gas costs common on EVM chains.`,
      gradientOnFocus: theme.mixins.raspberryGradient?.background as string,
    },
    {
      title: 'Q: Can Stellar applications integrate with existing systems?',
      description: `A: Yes, and it's one of our specialties. We regularly connect Stellar to legacy banking systems, payment processors, ERP platforms, and traditional databases using Horizon, Soroban RPC, and the official SDKs. We've also built our own indexing and data pipelines on top of raw chain data.`,
      gradientOnFocus: theme.mixins.cyanGradient?.background as string,
    },
    {
      title: 'Q: What about fiat on and off ramps?',
      description: `A: Stellar's anchor network is one of its biggest advantages. We integrate anchors using the relevant SEP standards (SEP-24 for deposits and withdrawals, SEP-31 for cross-border, SEP-10 for authentication) so your users can move between fiat and on-chain assets smoothly and compliantly.`,
      gradientOnFocus: theme.mixins.yellowGradient?.background as string,
    },
    {
      title: 'Q: What happens if regulations change after we launch?',
      description: `A: Stellar provides native compliance primitives, including authorization flags, clawback, trustlines, and SEP-based KYC/AML flows, that can be adjusted without rebuilding the whole system. We design regulatory adaptability into the architecture from the start, so you have room to adjust when the rules change.`,
      gradientOnFocus: theme.mixins.violetGradient?.background as string,
    },
  ];
};
